import { useEffect, useState } from "react";
import { signal, useSignal } from "./signal";

/**
 * سرعت‌گیرِ کارهایی که بقیه‌ی میز می‌بینند یا می‌شنوند — واکنش، عبارتِ آماده، چت، کلیدهای میزبان.
 *
 * آینه‌ی Backend/internal/pace: همان GCRA («هر every یکی، تا burst تا پشتِ هم») و همان «همان کلید،
 * دوباره» در پنجره‌ی again. اعدادِ هر جا همان اعدادِ سرور است، پس کلاینتِ سالم هرگز به دروازه‌ی سرور
 * نمی‌خورد و لمسِ پشتِ هم این‌جا همان لحظه کنار می‌رود — و دکمه کم‌رنگ می‌شود تا بازیکن بفهمد چرا.
 *
 * دروازه‌ی واقعی سرور است؛ این فقط برای این است که لمسِ دوم اصلاً نرود و دکمه حرفِ راست بزند.
 */
export class Pace {
  private tat = 0;
  private key = "";
  private keyAt = -Infinity;
  /** با هر پذیرش یکی بالا می‌رود — دکمه‌ها با [[useCool]] دوباره کشیده می‌شوند. */
  readonly taken = signal(0);

  constructor(
    readonly every: number,
    readonly burst: number,
    readonly again = 0,
    private readonly now: () => number = () => Date.now(),
  ) {}

  private slack(): number { return this.every * (Math.max(1, this.burst) - 1); }

  /** جا هست؟ اگر هست، سهمش برداشته می‌شود. key ِ خالی = تکرار مهم نیست، فقط شمار. */
  take(key = ""): boolean {
    const now = this.now();
    if (key && key === this.key && now - this.keyAt < this.again) return false;
    const tat = Math.max(this.tat, now);
    if (tat > now + this.slack()) return false;
    this.tat = tat + this.every;
    if (key) { this.key = key; this.keyAt = now; }
    this.taken.value = this.taken.value + 1;
    return true;
  }

  /** تا کِی (Date.now) این کلید خاموش است؛ ۰ = همین حالا آزاد. */
  until(key = ""): number {
    const now = this.now();
    let u = 0;
    if (key && key === this.key && now - this.keyAt < this.again) u = this.keyAt + this.again;
    const free = this.tat - this.slack();
    if (free > now) u = Math.max(u, free);
    return u;
  }

  /** همه‌چیز آزاد — صفحه‌ی تازه، میزِ تازه. */
  reset(): void {
    this.tat = 0;
    this.key = "";
    this.keyAt = -Infinity;
    this.taken.value = this.taken.value + 1;
  }
}

/**
 * دکمه‌ای که با یک Pace خاموش و روشن می‌شود: تا کِی خاموش است (۰ = آزاد). سرِ هر پذیرش و سرِ پایانِ
 * خاموشی دوباره کشیده می‌شود، پس کم‌رنگی خودش برمی‌گردد.
 */
export function useCool(p: Pace, key = ""): number {
  useSignal(p.taken);
  const [, bump] = useState(0);
  const until = p.until(key);
  useEffect(() => {
    if (!until) return;
    const id = setTimeout(() => bump((x) => x + 1), Math.max(0, until - Date.now()) + 20);
    return () => clearTimeout(id);
  }, [until]);
  return until;
}

/** کلاس و سبکِ دکمه‌ی در حالِ خنک‌شدن: کم‌رنگ، و نوارِ زیرش تا پایانِ خاموشی پر می‌شود. */
export function coolProps(until: number): { className: string; style?: Record<string, string> } {
  if (!until) return { className: "" };
  const ms = Math.max(0, until - Date.now());
  return { className: " cooling", style: { ["--cool"]: `${ms}ms` } };
}
